/**
 * 投稿テンプレート選択モーダル（2026-07-25 追加）。
 * - 標準テンプレート（BUILTIN_BOARD_TEMPLATES）+ カスタムテンプレート（Firestore）を一覧。
 * - 行クリックで件名・本文（+ タイプ・緊急度）をフォームへ適用。
 * - 「＋ テンプレートを追加」から現在の入力内容 or 手入力でカスタムテンプレートを保存。
 * - 本文プレビューはプレーンテキスト化して表示（生 HTML を dangerouslySetInnerHTML しない）。
 */
import type { BoardPostType, BoardUrgency } from '@goenhub/shared';
import { BOARD_POST_TYPE_LABEL, BOARD_URGENCY_LABEL } from '@goenhub/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useBoardTemplates,
  useCreateBoardTemplate,
  useDeleteBoardTemplate,
} from '@/hooks/useBoardTemplates';

import { BUILTIN_BOARD_TEMPLATES } from '../boardTemplates';
import { BOARD_TYPE_VARIANT } from '../boardMeta';

/** 適用時にフォームへ渡す値。 */
export interface AppliedTemplate {
  subject: string;
  bodyHtml: string;
  type?: BoardPostType;
  urgency?: BoardUrgency;
}

interface TemplateRow {
  id: string;
  name: string;
  description?: string;
  builtin: boolean;
  createdBy?: string;
  type?: BoardPostType;
  urgency?: BoardUrgency;
  subject: string;
  bodyHtml: string;
}

/** HTML → プレーンテキスト（プレビュー用の安全な抜粋）。 */
function toPlainText(html: string): string {
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export function TemplatePickerDialog({
  open,
  onOpenChange,
  onApply,
  currentSubject,
  currentBodyHtml,
  currentType,
  currentUrgency,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onApply: (t: AppliedTemplate) => void;
  /** 「現在の入力から作成」用の初期値。 */
  currentSubject: string;
  currentBodyHtml: string;
  currentType: BoardPostType;
  currentUrgency: BoardUrgency;
}) {
  const { user } = useAuth();
  const templates = useBoardTemplates();
  const createTemplate = useCreateBoardTemplate();
  const deleteTemplate = useDeleteBoardTemplate();

  const [adding, setAdding] = useState(false);
  const [formName, setFormName] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formBody, setFormBody] = useState('');

  const rows: TemplateRow[] = useMemo(() => {
    const builtins: TemplateRow[] = BUILTIN_BOARD_TEMPLATES.map((t) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      builtin: true,
      type: t.type,
      urgency: t.urgency,
      subject: t.subject,
      bodyHtml: t.bodyHtml,
    }));
    const customs: TemplateRow[] = (templates.data ?? []).map((t) => ({
      id: t.id,
      name: t.name,
      builtin: false,
      createdBy: t.createdBy,
      type: t.type,
      urgency: t.urgency,
      subject: t.subject,
      bodyHtml: t.bodyHtml,
    }));
    return [...builtins, ...customs];
  }, [templates.data]);

  const isOwner = user?.isOwner === true;

  const openAddForm = () => {
    // 現在の入力があればそれを初期値にする（無ければ空）
    setFormName('');
    setFormSubject(currentSubject);
    setFormBody(currentBodyHtml);
    setAdding(true);
  };

  const handleCreate = async () => {
    const name = formName.trim();
    if (!name) {
      toast.error('テンプレート名を入力してください');
      return;
    }
    if (!formSubject.trim() && !formBody.trim()) {
      toast.error('件名または本文を入力してください');
      return;
    }
    try {
      await createTemplate.mutateAsync({
        name,
        subject: formSubject,
        bodyHtml: formBody,
      });
      toast.success(`テンプレート「${name}」を追加しました`);
      setAdding(false);
    } catch (err) {
      toast.error(`追加失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDelete = async (row: TemplateRow) => {
    if (!window.confirm(`テンプレート「${row.name}」を削除しますか？`)) return;
    try {
      await deleteTemplate.mutateAsync(row.id);
      toast.success('テンプレートを削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>テンプレートから選択</DialogTitle>
        </DialogHeader>

        {adding ? (
          <div className="space-y-3">
            <p className="text-xs text-[var(--text-muted)]">
              現在の件名・本文を初期値にしています。名前を付けて保存すると次回から一覧に表示されます。
            </p>
            <div>
              <Label>テンプレート名 *</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="例: 週次ミーティング議事録"
                autoFocus
              />
            </div>
            <div>
              <Label>件名</Label>
              <Input
                value={formSubject}
                onChange={(e) => setFormSubject(e.target.value)}
                placeholder="件名テンプレート"
              />
            </div>
            <div>
              <Label>本文（プレーンテキスト / 簡易 HTML）</Label>
              <Textarea
                value={formBody}
                onChange={(e) => setFormBody(e.target.value)}
                rows={6}
                placeholder="本文テンプレート"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
                戻る
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => void handleCreate()}
                disabled={createTemplate.isPending}
              >
                保存
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 flex flex-col gap-1.5">
              {templates.isLoading ? (
                <div className="flex justify-center p-6">
                  <Spinner />
                </div>
              ) : (
                rows.map((row) => (
                  <div
                    key={row.id}
                    className="group flex items-start gap-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 hover:border-[var(--accent)] transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onApply({
                          subject: row.subject,
                          bodyHtml: row.bodyHtml,
                          type: row.type,
                          urgency: row.urgency,
                        });
                        onOpenChange(false);
                      }}
                      className="flex-1 text-left min-w-0"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[var(--text-primary)]">
                          {row.name}
                        </span>
                        {row.builtin ? (
                          <Badge variant="default">標準</Badge>
                        ) : (
                          <Badge variant="accent">カスタム</Badge>
                        )}
                        {row.type && (
                          <Badge variant={BOARD_TYPE_VARIANT[row.type]}>
                            {BOARD_POST_TYPE_LABEL[row.type]}
                          </Badge>
                        )}
                        {row.urgency && (
                          <span className="text-[11px] text-[var(--text-muted)]">
                            緊急度 {BOARD_URGENCY_LABEL[row.urgency]}
                          </span>
                        )}
                      </div>
                      {row.description && (
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                          {row.description}
                        </p>
                      )}
                      <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-1">
                        {row.subject} — {toPlainText(row.bodyHtml)}
                      </p>
                    </button>
                    {!row.builtin && (isOwner || row.createdBy === user?.uid) && (
                      <button
                        type="button"
                        aria-label="テンプレートを削除"
                        onClick={() => void handleDelete(row)}
                        className="shrink-0 p-1 rounded text-[var(--text-muted)] hover:text-[var(--accent-red)] opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={openAddForm}>
                <Plus className="size-4 mr-1" />
                テンプレートを追加
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
